import { useEffect } from 'react';

import { observer } from 'mobx-react';

import { Loader } from '../../../components/Loader/Loader';
import { AppLogo } from '../../../components/SVGIcons/AppLogo/AppLogo';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { useImagesCarousel } from '../../../hooks/useImagesCarousel';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';

import { BannerCarousel } from './components/BannerCarousel';
import { SignupSection } from './components/SignupSection';
import { defaultLanderPrimaryCarouselImg } from './constants';
import classes from './DirectRegLanding.module.scss';

interface DirectRegLandingProps extends WizardChildProps {
  initialMode?: 'signup' | 'login';
}

const DirectRegLanding = observer((props: DirectRegLandingProps) => {
  const { initialMode, ...wizardProps } = props;
  const { authStore } = useStores();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { isSigningUp } = authStore;
  const isLoading = authStore.pending('signIn') || isSigningUp;

  const directRegLandingImagesUrls = useFlag<string[]>('direct_registration_landing_images', []);

  const landingBannerImage = authStore.getDirectSignUpLanderBanner();
  const primaryImage = landingBannerImage || defaultLanderPrimaryCarouselImg;

  const { carouselImages, currentIndex, currentImage, goToSlide, hasMultipleImages } = useImagesCarousel({
    imagesUrls: directRegLandingImagesUrls,
    primaryImage,
  });

  useEffect(() => {
    void analytics.sendEvent('landing_page', {
      event_action: 'impression',
      landing_type: 'direct_reg',
      utm_promo: authStore.getUtmPromo(),
    });
  }, [authStore]);

  return (
    <div className={classes.directRegLandingContainer}>
      {isLoading && <Loader fullScreen />}

      <div className={classes.directRegLanding}>
        <div className={classes.logoSection}>
          <AppLogo
            alwaysColorful
            withText
            textClassname={classes.logoText}
            textColor="#fff"
            width={isMobile ? 58 : 82}
          />
        </div>
        <div className={classes.logoAndBannerContainer}>
          <div className={classes.bannerSection}>
            <BannerCarousel
              carouselImages={carouselImages}
              currentImage={currentImage}
              currentIndex={currentIndex}
              goToSlide={goToSlide}
              hasMultipleImages={hasMultipleImages}
            />
          </div>
        </div>

        <SignupSection
          initialMode={initialMode}
          wizardProps={wizardProps}
          onLoginNeedsVerification={wizardProps.nextStep}
          onSignupSuccess={wizardProps.nextStep}
        />
      </div>
    </div>
  );
});

export default DirectRegLanding;
